

import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Legend as PieLegend, LineChart, Line } from 'recharts';
import { Transaction, Kpi, TimeEntry, ProrationRule, Due, Subscription } from '../types';
import KpiCard from './KpiCard';
import { CategoryIcon, DuesIcon, SubscriptionIcon } from './icons';
import { formatCurrency } from '../utils/formatting';


const COLORS_EXPENSE = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6', '#d946ef'];
const COLORS_INCOME = ['#10B981', '#22C55E', '#84CC16', '#3B82F6', '#6366F1', '#EC4899', '#8B5CF6'];
const COLORS_HOURS = ['#10B981', '#3B82F6', '#F59E0B', '#6366F1', '#EC4899', '#8B5CF6'];
const COLORS_PAYMENT = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1'];

interface DashboardViewProps {
    transactions: Transaction[];
    dues: Due[];
    subscriptions: Subscription[];
    categories: string[];
    timeEntries: TimeEntry[];
    uncategorizedCount: number;
    onStartReview: () => void;
    prorationRules: ProrationRule[];
    theme: 'light' | 'dark';
    onKpiCardClick: (filter: 'income' | 'expense' | 'all') => void;
    currency: string;
}

const formatDecimalHours = (decimalHours: number): string => {
    if (isNaN(decimalHours) || decimalHours < 0) return "0min";
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}min`;
    } else if (hours > 0) {
        return `${hours}h`;
    } else {
        return `${minutes}min`;
    }
};

const CustomLegend = (props: any) => {
    const { payload } = props;

    return (
        <div className="text-xs h-full flex flex-col justify-center pl-2">
            {payload.map((entry: any, index: number) => (
                <div key={`item-${index}`} className="flex items-start mb-1">
                    <div
                        className="w-2.5 h-2.5 rounded-full mr-2 flex-shrink-0 mt-1"
                        style={{ backgroundColor: entry.color }}
                    />
                    <span className="break-words text-light-text-secondary dark:text-text-secondary">
                        {entry.value}
                    </span>
                </div>
            ))}
        </div>
    );
};


const FilteredTransactionsList: React.FC<{ transactions: Transaction[], currency: string }> = ({ transactions, currency }) => {
    const totalIncome = useMemo(() => transactions.filter(t => t.amount > 0).reduce((acc, t) => acc + t.amount, 0), [transactions]);
    const totalExpenses = useMemo(() => transactions.filter(t => t.amount < 0).reduce((acc, t) => acc + t.amount, 0), [transactions]);
    const total = useMemo(() => totalIncome + totalExpenses, [totalIncome, totalExpenses]);

    return (
        <div className="mt-8 bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
            <h3 className="font-bold text-lg text-light-text-primary dark:text-text-primary mb-4">Movimientos Filtrados</h3>
            <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm text-left text-light-text-secondary dark:text-gray-400">
                    <thead className="text-xs text-light-text-secondary dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0">
                        <tr>
                            <th className="px-4 py-3">Fecha</th>
                            <th className="px-4 py-3">Concepto</th>
                            <th className="px-4 py-3 text-right">Importe</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transactions.map(t => (
                            <tr key={t.id} className="bg-light-surface dark:bg-surface border-b border-light-border dark:border-border hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-3 whitespace-nowrap">{t.date}</td>
                                <td className="px-4 py-3">{t.concept}</td>
                                <td className={`px-4 py-3 text-right font-medium ${t.amount > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {formatCurrency(t.amount, currency)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className="sticky bottom-0">
                         <tr className="font-semibold bg-gray-100 dark:bg-gray-700 text-light-text-primary dark:text-text-primary">
                             <td colSpan={2} className="px-4 py-2 text-right border-t border-light-border dark:border-gray-600">Total Ingresos:</td>
                             <td className="px-4 py-2 text-right text-green-500 border-t border-light-border dark:border-gray-600">
                                 {formatCurrency(totalIncome, currency)}
                             </td>
                         </tr>
                         <tr className="font-semibold bg-gray-100 dark:bg-gray-700 text-light-text-primary dark:text-text-primary">
                             <td colSpan={2} className="px-4 py-2 text-right">Total Gastos:</td>
                             <td className="px-4 py-2 text-right text-red-500">
                                 {formatCurrency(totalExpenses, currency)}
                             </td>
                         </tr>
                        <tr className="font-bold bg-gray-200 dark:bg-gray-800 text-light-text-primary dark:text-text-primary text-base">
                            <td colSpan={2} className="px-4 py-3 text-right border-t-2 border-light-border dark:border-border">Total Filtrado:</td>
                            <td className={`px-4 py-3 text-right ${total >= 0 ? 'text-green-500' : 'text-red-500'} border-t-2 border-light-border dark:border-border`}>
                                {formatCurrency(total, currency)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

const UpcomingDues: React.FC<{ dues: Due[], currency: string }> = ({ dues, currency }) => {
    const upcoming = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const reminderDaysMap = {
            '1day': 1,
            '2days': 2,
            '1week': 7,
            'none': -1
        };

        return dues
            .filter(due => due.status === 'active')
            .map(due => {
                const startDate = new Date(due.startDate);
                let nextPaymentMonth = startDate.getMonth() + due.installmentsPaid;
                let nextPaymentYear = startDate.getFullYear();
                
                if (nextPaymentMonth >= 12) {
                    nextPaymentYear += Math.floor(nextPaymentMonth / 12);
                    nextPaymentMonth = nextPaymentMonth % 12;
                }
                
                let nextPaymentDate = new Date(nextPaymentYear, nextPaymentMonth, due.paymentDayOfMonth);

                // If next payment date is in the past for the current month, calculate for the next month
                if(nextPaymentDate < today){
                     nextPaymentMonth += 1;
                     if (nextPaymentMonth >= 12) {
                        nextPaymentYear += Math.floor(nextPaymentMonth / 12);
                        nextPaymentMonth = nextPaymentMonth % 12;
                    }
                    nextPaymentDate = new Date(nextPaymentYear, nextPaymentMonth, due.paymentDayOfMonth);
                }

                const diffTime = nextPaymentDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                const reminderDays = reminderDaysMap[due.reminder];
                const isUpcoming = reminderDays !== -1 && diffDays >= 0 && diffDays <= reminderDays;

                const paymentAmount = due.paymentType === 'single' ? due.totalAmount : (due.installmentsTotal > 0 ? due.totalAmount / due.installmentsTotal : 0);

                return { ...due, nextPaymentDate, diffDays, isUpcoming, paymentAmount };
            })
            .filter(due => due.isUpcoming)
            .sort((a, b) => a.nextPaymentDate.getTime() - b.nextPaymentDate.getTime());

    }, [dues]);

    if (upcoming.length === 0) {
        return null;
    }

    return (
        <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
            <div className="flex items-center gap-3 mb-4">
                <DuesIcon className="w-6 h-6 text-accent-rose"/>
                <h3 className="font-bold text-lg text-light-text-primary dark:text-text-primary">Próximos Vencimientos</h3>
            </div>
            <div className="space-y-3">
                {upcoming.map(due => (
                    <div key={due.id} className="flex justify-between items-center bg-gray-100 dark:bg-gray-800/50 p-3 rounded-md">
                        <div>
                            <p className="font-semibold text-light-text-primary dark:text-text-primary">{due.name}</p>
                            <p className="text-sm text-light-text-secondary dark:text-text-secondary">
                                Vence en {due.diffDays} {due.diffDays === 1 ? 'día' : 'días'} ({due.nextPaymentDate.toLocaleDateString('es-ES')})
                            </p>
                        </div>
                        <div className="text-right">
                             <p className="font-bold text-red-500">{formatCurrency(due.paymentAmount, currency)}</p>
                             {due.paymentType === 'installments' && (
                                <p className="text-xs text-light-text-secondary dark:text-text-secondary">
                                    {due.installmentsPaid + 1}/{due.installmentsTotal}
                                </p>
                             )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const DashboardView: React.FC<DashboardViewProps> = ({ transactions, dues, subscriptions, categories, timeEntries, uncategorizedCount, onStartReview, prorationRules, theme, onKpiCardClick, currency }) => {
    const [includeVAT, setIncludeVAT] = useState(true);
    const [selectedVATRate, setSelectedVATRate] = useState(21);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [showDues, setShowDues] = useState(true);
    const [showSubscriptions, setShowSubscriptions] = useState(true);
    
    const chartColors = {
        tick: theme === 'dark' ? '#9CA3AF' : '#4B5563',
        grid: theme === 'dark' ? '#374151' : '#E5E7EB',
        tooltip: {
            backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
            border: `1px solid ${theme === 'dark' ? '#374151' : '#E5E7EB'}`,
            color: theme === 'dark' ? '#F9FAFB' : '#111827',
        },
        cursor: theme === 'dark' ? '#37415180' : '#E5E7EB80',
    };

    const allCategories = useMemo(() => {
        return [...categories].sort();
    }, [categories]);

    const handleCategoryChange = (category: string) => {
        setSelectedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category)
                : [...prev, category]
        );
    };

    const committedExpensesData = useMemo(() => {
        const data = [];
        const today = new Date();
        for (let i = 0; i < 12; i++) {
            const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
            data.push({
                name: date.toLocaleString('es-ES', { month: 'short', year: '2-digit' }),
                date,
                vencimientos: 0,
                suscripciones: 0,
            });
        }

        if (showDues) {
            dues.forEach(due => {
                if (due.status !== 'active') return;
                const paymentAmount = due.paymentType === 'single' ? due.totalAmount : (due.installmentsTotal > 0 ? due.totalAmount / due.installmentsTotal : 0);
                if (paymentAmount <= 0) return;

                const startDate = new Date(due.startDate);
                for (let i = due.installmentsPaid; i < due.installmentsTotal; i++) {
                    const paymentDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, due.paymentDayOfMonth);
                    const monthData = data.find(m => m.date.getFullYear() === paymentDate.getFullYear() && m.date.getMonth() === paymentDate.getMonth());
                    if (monthData) {
                        monthData.vencimientos += paymentAmount;
                    }
                }
            });
        }
        
        if (showSubscriptions) {
            subscriptions.forEach(sub => {
                if (sub.status !== 'active') return;
                
                let monthlyCost = 0;
                switch (sub.billingCycle) {
                    case 'monthly': monthlyCost = sub.amount; break;
                    case 'quarterly': monthlyCost = sub.amount / 3; break;
                    case 'yearly': monthlyCost = sub.amount / 12; break;
                }

                if (monthlyCost <= 0) return;

                const nextBillingDate = new Date(sub.nextBillingDate);
                const cancellationDate = sub.cancellationDate !== 'never' ? new Date(sub.cancellationDate) : null;

                data.forEach(monthData => {
                    // Check if subscription is active during this month
                    const monthEnd = new Date(monthData.date.getFullYear(), monthData.date.getMonth() + 1, 0);
                    if (monthEnd >= nextBillingDate && (!cancellationDate || monthData.date < cancellationDate)) {
                        monthData.suscripciones += monthlyCost;
                    }
                });
            });
        }
        
        return data.map(d => ({
            ...d,
            vencimientos: parseFloat(d.vencimientos.toFixed(2)),
            suscripciones: parseFloat(d.suscripciones.toFixed(2))
        }));

    }, [dues, subscriptions, showDues, showSubscriptions]);

    const { kpis, expenseBreakdownData, incomeBreakdownData, categoryHourlyRateData, hoursBreakdownData, cashflowData, paymentMethodUsageData, filteredTransactions } = useMemo(() => {
        const VAT_MULTIPLIER = 1 + (selectedVATRate / 100);

        const adjustForVAT = (transaction: Transaction): number => {
            if (includeVAT) {
                return transaction.amount;
            }
            
            if (selectedCategories.length > 0) {
                const isInFilter = transaction.category.some(c => selectedCategories.includes(c));
                if (isInFilter) {
                    return transaction.amount / VAT_MULTIPLIER;
                }
                return transaction.amount;
            }
            
            return transaction.amount / VAT_MULTIPLIER;
        };

        const transactionsWithVAT = transactions.map(t => ({...t, amount: adjustForVAT(t)}));

        const baseTransactions = selectedCategories.length > 0 
            ? transactionsWithVAT.filter(t => t.category.some(c => selectedCategories.includes(c)))
            : transactionsWithVAT;

        const totalIncome = baseTransactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
        const totalExpenses = baseTransactions.filter(t => t.amount < 0).reduce((sum, t) => sum + t.amount, 0);
        const profit = totalIncome + totalExpenses;
        const margin = totalIncome > 0 ? (profit / totalIncome) * 100 : 0;
        
        const totalHours = timeEntries.reduce((sum, t) => sum + t.hours, 0);
        const hourlyRate = totalHours > 0 ? profit / totalHours : 0;

        const dynamicKpis: Kpi[] = [
            { title: "Ingresos", value: formatCurrency(totalIncome, currency)},
            { title: "Gastos", value: formatCurrency(totalExpenses, currency)},
            { title: "Beneficio", value: formatCurrency(profit, currency)},
            { title: "Margen", value: `${margin.toFixed(1)}%` },
            { title: "Valor/Hora", value: formatCurrency(hourlyRate, currency) }, 
        ];

        const allExpenseTransactions = transactionsWithVAT.filter(t => t.amount < 0);

        const expenseTotals = new Map<string, number>();
        allExpenseTransactions.forEach(t => {
            const rule = prorationRules.find(r => r.sourceCategory === t.category[0]);
            if (rule) {
                rule.targets.forEach(target => {
                    const proratedAmount = -t.amount * (target.percentage / 100);
                    const currentTotal = expenseTotals.get(target.category) || 0;
                    expenseTotals.set(target.category, currentTotal + proratedAmount);
                });
            } else {
                const currentTotal = expenseTotals.get(t.category[0]) || 0;
                expenseTotals.set(t.category[0], currentTotal - t.amount);
            }
        });
        
        let dynamicExpenseBreakdown: { name: string, value: number }[] = [];
        const expenseCategoriesToAnalyze = selectedCategories.length > 0 ? selectedCategories : allCategories.filter(c => !c.toLowerCase().includes('ingresos') && c !== "Desconocida");
        
        expenseCategoriesToAnalyze.forEach(category => {
            const categoryExpenses = expenseTotals.get(category) || 0;
            if (categoryExpenses > 0) {
                dynamicExpenseBreakdown.push({ name: category, value: parseFloat(categoryExpenses.toFixed(2)) });
            }
        });
        
        const allIncomeTransactions = transactionsWithVAT.filter(t => t.amount > 0);
        let dynamicIncomeBreakdown: { name: string, value: number }[] = [];
        const incomeCategoriesToAnalyze = selectedCategories.length > 0 ? selectedCategories : allCategories.filter(c => c.toLowerCase().includes('ingresos') && c !== "Desconocida");

        incomeCategoriesToAnalyze.forEach(category => {
            const categoryIncome = allIncomeTransactions.filter(t => t.category.includes(category)).reduce((sum, t) => sum + t.amount, 0);
            if (categoryIncome > 0) {
                dynamicIncomeBreakdown.push({ name: category, value: parseFloat(categoryIncome.toFixed(2)) });
            }
        });
        
        const categoryHours: { [key: string]: number } = {};
        timeEntries.forEach(entry => {
            categoryHours[entry.category] = (categoryHours[entry.category] || 0) + entry.hours;
        });

        const categoryProfits: { [key: string]: number } = {};
        transactionsWithVAT.forEach(t => {
            t.category.forEach(cat => {
                 categoryProfits[cat] = (categoryProfits[cat] || 0) + t.amount;
            });
        });
        
        const dynamicCategoryHourlyRateData = Object.keys(categoryHours).map(category => {
            const hours = categoryHours[category];
            const profit = categoryProfits[category] || 0;
            const rate = hours > 0 ? profit / hours : 0;
            return { name: category, rate: parseFloat(rate.toFixed(2)) };
        }).filter(c => c.rate !== 0 || categoryHours[c.name] > 0);


        const dynamicHoursBreakdownData: { [key: string]: number } = {};
        timeEntries.forEach(entry => {
            dynamicHoursBreakdownData[entry.category] = (dynamicHoursBreakdownData[entry.category] || 0) + entry.hours;
        });
        const formattedHoursBreakdown = Object.entries(dynamicHoursBreakdownData).map(([name, value]) => ({ name, value: parseFloat(value.toFixed(2)) }));

        const dailyChanges = new Map<string, number>();
        baseTransactions.forEach(t => {
            const date = t.date;
            dailyChanges.set(date, (dailyChanges.get(date) || 0) + t.amount);
        });

        const sortedUniqueDates = Array.from(dailyChanges.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        let runningBalance = 0;
        const dynamicCashflowData = sortedUniqueDates.map(date => {
            runningBalance += dailyChanges.get(date) || 0;
            return {
                date,
                balance: parseFloat(runningBalance.toFixed(2)),
            };
        });

        const methodTotals: { [key: string]: number } = {};
        baseTransactions.forEach(t => {
            const method = t.paymentMethod || 'No especificado';
            methodTotals[method] = (methodTotals[method] || 0) + Math.abs(t.amount);
        });
        const dynamicPaymentMethodUsageData = Object.entries(methodTotals)
            .map(([name, value]) => ({ name, value: parseFloat(value.toFixed(2)) }))
            .sort((a, b) => b.value - a.value);

        return { 
            kpis: dynamicKpis, 
            expenseBreakdownData: dynamicExpenseBreakdown.sort((a,b) => b.value - a.value),
            incomeBreakdownData: dynamicIncomeBreakdown.sort((a,b) => b.value - a.value),
            categoryHourlyRateData: dynamicCategoryHourlyRateData,
            hoursBreakdownData: formattedHoursBreakdown,
            cashflowData: dynamicCashflowData,
            paymentMethodUsageData: dynamicPaymentMethodUsageData,
            filteredTransactions: baseTransactions
        };

    }, [transactions, selectedCategories, allCategories, timeEntries, prorationRules, includeVAT, selectedVATRate, currency]);

    const kpiClickHandlers: Record<string, (() => void) | undefined> = {
        'Ingresos': () => onKpiCardClick('income'),
        'Gastos': () => onKpiCardClick('expense'),
        'Beneficio': () => onKpiCardClick('all'),
    };
    
    const legendWrapperStyle = { width: 130 };

    return (
        <div className="space-y-6 lg:space-y-8">
            {uncategorizedCount > 0 && (
                <div id="uncategorized-banner" className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 p-4 rounded-r-lg dark:bg-yellow-500/10 dark:border-yellow-500/30 dark:text-yellow-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <CategoryIcon className="w-8 h-8 flex-shrink-0 text-yellow-500 dark:text-yellow-200" />
                        <div>
                            <h3 className="font-bold">Tienes {uncategorizedCount} transacciones por categorizar.</h3>
                            <p className="text-sm text-yellow-700 dark:text-yellow-300/80">Revisarlas mejorará la precisión de tus informes.</p>
                        </div>
                    </div>
                    <button onClick={onStartReview} className="bg-primary text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-600 transition-colors shadow-md w-full sm:w-auto flex-shrink-0">
                        Revisar ahora
                    </button>
                </div>
            )}
            
            <UpcomingDues dues={dues} currency={currency} />

            <div id="kpi-cards-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                 {kpis.map((kpi) => (
                    <KpiCard key={kpi.title} {...kpi} onClick={kpiClickHandlers[kpi.title]}/>
                ))}
            </div>

            <div className="bg-light-surface dark:bg-surface p-4 rounded-lg border border-light-border dark:border-border">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                    <div>
                        <label className="font-medium text-light-text-secondary dark:text-text-secondary block mb-3">Filtrar por Categoría:</label>
                        <div className="flex flex-wrap gap-2">
                            {allCategories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => handleCategoryChange(category)}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${selectedCategories.includes(category) ? 'bg-primary text-white ring-2 ring-offset-2 ring-light-surface dark:ring-offset-surface ring-primary' : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-light-text-secondary dark:text-text-secondary'}`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center flex-shrink-0 self-end sm:self-start">
                        <label htmlFor="vat-toggle" className="mr-3 font-medium text-light-text-secondary dark:text-text-secondary">
                            Incluir IVA
                        </label>
                        <div onClick={() => setIncludeVAT(!includeVAT)} className={`relative inline-flex items-center h-6 rounded-full w-11 cursor-pointer transition-colors duration-300 ${includeVAT ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-300 ${includeVAT ? 'translate-x-6' : 'translate-x-1'}`} />
                        </div>
                         <div className="ml-4">
                            <select
                                value={selectedVATRate}
                                onChange={(e) => setSelectedVATRate(Number(e.target.value))}
                                disabled={includeVAT}
                                className="bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label="Seleccionar tipo de IVA"
                            >
                                <option value="21">IVA 21%</option>
                                <option value="10">IVA 10%</option>
                                <option value="4">IVA 4%</option>
                                <option value="0">Exento 0%</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div id="main-dashboard-charts" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                 <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                    <h3 className="font-bold text-lg mb-4 text-light-text-primary dark:text-text-primary">Top Gastos {selectedCategories.length > 0 ? `(${selectedCategories.join(', ')})` : '(Global)'}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={expenseBreakdownData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={80} paddingAngle={5} fill="#8884d8">
                                {expenseBreakdownData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS_EXPENSE[index % COLORS_EXPENSE.length]} />
                                ))}
                            </Pie>
                            <Tooltip 
                                contentStyle={chartColors.tooltip}
                                itemStyle={{ color: chartColors.tooltip.color }}
                                formatter={(value: number) => formatCurrency(value, currency)} 
                            />
                            <Legend
                                content={<CustomLegend />}
                                verticalAlign="middle"
                                align="right"
                                wrapperStyle={legendWrapperStyle}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                    <h3 className="font-bold text-lg mb-4 text-light-text-primary dark:text-text-primary">Top Ingresos {selectedCategories.length > 0 ? `(${selectedCategories.join(', ')})` : '(Global)'}</h3>
                     <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={incomeBreakdownData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={80} paddingAngle={5} fill="#82ca9d">
                                {incomeBreakdownData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS_INCOME[index % COLORS_INCOME.length]} />
                                ))}
                            </Pie>
                             <Tooltip 
                                contentStyle={chartColors.tooltip} 
                                itemStyle={{ color: chartColors.tooltip.color }}
                                formatter={(value: number) => formatCurrency(value, currency)} 
                            />
                            <Legend
                                content={<CustomLegend />}
                                verticalAlign="middle"
                                align="right"
                                wrapperStyle={legendWrapperStyle}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
            
            {selectedCategories.length > 0 && <FilteredTransactionsList transactions={filteredTransactions} currency={currency} />}
            
             <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                <h3 className="font-bold text-lg mb-4 text-light-text-primary dark:text-text-primary">Flujo de Caja (Cashflow)</h3>
                {cashflowData.length > 1 ? (
                    <ResponsiveContainer width="100%" height={300}>
                       <LineChart data={cashflowData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                            <XAxis dataKey="date" stroke={chartColors.tick} tick={{ fontSize: 12 }} />
                            <YAxis stroke={chartColors.tick} tickFormatter={(value) => formatCurrency(value, currency, { style: 'decimal' })} />
                            <Tooltip
                                contentStyle={chartColors.tooltip}
                                itemStyle={{ color: chartColors.tooltip.color }}
                                formatter={(value: number) => [formatCurrency(value, currency), "Saldo"]}
                            />
                            <Line type="monotone" dataKey="balance" stroke="#3B82F6" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 6 }} name="Saldo" />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-[300px] text-light-text-secondary dark:text-text-secondary">
                        Se necesitan más transacciones para mostrar el flujo de caja.
                    </div>
                )}
            </div>
            
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                    <h3 className="font-bold text-lg text-light-text-primary dark:text-text-primary">Gastos Mensuales Comprometidos</h3>
                    <div className="flex items-center gap-2 mt-3 sm:mt-0">
                        <button onClick={() => setShowDues(!showDues)} className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${showDues ? 'bg-accent-rose text-white' : 'bg-gray-200 dark:bg-gray-700 text-light-text-secondary dark:text-text-secondary'}`}>
                            <DuesIcon className="w-4 h-4" /> Vencimientos
                        </button>
                        <button onClick={() => setShowSubscriptions(!showSubscriptions)} className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${showSubscriptions ? 'bg-accent-amber text-white' : 'bg-gray-200 dark:bg-gray-700 text-light-text-secondary dark:text-text-secondary'}`}>
                             <SubscriptionIcon className="w-4 h-4" /> Suscripciones
                        </button>
                    </div>
                </div>
                 <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={committedExpensesData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis dataKey="name" stroke={chartColors.tick} tick={{ fontSize: 12 }} />
                        <YAxis stroke={chartColors.tick} tickFormatter={(value) => formatCurrency(value, currency, { style: 'decimal' })} />
                        <Tooltip 
                            contentStyle={chartColors.tooltip}
                            cursor={{ fill: chartColors.cursor }}
                            formatter={(value: number) => formatCurrency(value, currency)}
                        />
                        <Legend />
                        <Bar dataKey="vencimientos" stackId="a" fill="#f43f5e" name="Vencimientos" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="suscripciones" stackId="a" fill="#eab308" name="Suscripciones" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                <h3 className="font-bold text-lg mb-4 text-light-text-primary dark:text-text-primary">Valor/Hora por Categoría</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={categoryHourlyRateData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis dataKey="name" stroke={chartColors.tick} />
                        <YAxis stroke={chartColors.tick} tickFormatter={(value) => formatCurrency(value, currency, { style: 'decimal' })} />
                        <Tooltip 
                            contentStyle={chartColors.tooltip} 
                            itemStyle={{ color: chartColors.tooltip.color }}
                            labelStyle={{ color: chartColors.tooltip.color }}
                            cursor={{ fill: chartColors.cursor }} 
                            formatter={(value: number) => formatCurrency(value, currency)}
                        />
                        <Legend wrapperStyle={{ color: chartColors.tick }}/>
                        <Bar dataKey="rate" name="Valor/Hora">
                            {categoryHourlyRateData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.rate >= 0 ? '#3B82F6' : '#EF4444'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
             
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                    <h3 className="font-bold text-lg mb-4 text-light-text-primary dark:text-text-primary">Uso de Métodos de Pago</h3>
                     <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={paymentMethodUsageData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={80} fill="#8884d8" paddingAngle={5}>
                                {paymentMethodUsageData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS_PAYMENT[index % COLORS_PAYMENT.length]} />
                                ))}
                            </Pie>
                            <Tooltip 
                                contentStyle={chartColors.tooltip}
                                itemStyle={{ color: chartColors.tooltip.color }}
                                formatter={(value: number) => formatCurrency(value, currency)}
                            />
                            <Legend
                                content={<CustomLegend />}
                                verticalAlign="middle"
                                align="right"
                                wrapperStyle={legendWrapperStyle}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                 <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                    <h3 className="font-bold text-lg mb-4 text-light-text-primary dark:text-text-primary">Horas por Categoría</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={hoursBreakdownData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={80} fill="#8884d8" paddingAngle={5}>
                                {hoursBreakdownData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS_HOURS[index % COLORS_HOURS.length]} />
                                ))}
                            </Pie>
                            <Tooltip 
                                contentStyle={chartColors.tooltip} 
                                itemStyle={{ color: chartColors.tooltip.color }}
                                formatter={(value: number) => formatDecimalHours(value)} 
                            />
                            <Legend
                                content={<CustomLegend />}
                                verticalAlign="middle"
                                align="right"
                                wrapperStyle={legendWrapperStyle}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default DashboardView;